import { useState } from 'react';
import { infoToolTip } from '../../assets/hostedassets';
import LoadingSpinner from '../../common/_custom/LoadingSpinner/LoadingSpinner';
import FetchFailureIcon from '../../common/_custom/FetchFailureIcon/FetchFailureIcon';
import { FETCH_FAILURE_TEXT } from '../../utils/constants/taskTrackerStatus';
import styles from './RiskIndicators.module.scss';
import RiskIndicatorTable from './RiskIndicatorTable/RiskIndicatorTable';
import Tooltip from '../../common/_custom/Tooltip/Tooltip';
import { RISK_HEADER_KEYS, RISK_MESSAGES_MAP } from './RiskIndicatorConstants';
import { LEGAL_CASES_DIRECTION_BY_KEY, LEGAL_CASES_KEYS } from './legalCasesRiskState';
import type { Direction, GroupId } from '../Dashboard/components/DashboardTables/LegalCases/legalCasesType';
import clsx from 'clsx';
import { useRiskIndicatorStates } from './useRiskIndicatorStates';
import A4Page from '../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../store/pdfReport/pdfReport_constants';
import RiskIndicatorCard from './RiskIndicatorCard';
import HeaderBodyBox from '../../common/HeaderBodyBox/HeaderBodyBox';
import AskAicaQuestionBadge from '../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';
import RegulatoryRiskDrawer from './RegulatoryRiskDrawer/RegulatoryRiskDrawer';
import type { DirectorHoverProfile } from '../Dashboard/DashboardModules/RelatedParties/components/DirectorHoverCard/DirectorHoverCard.types';

interface RiskProfile {
    [key: string]: number;
}

/*
    RECUR-27829: one cell of the per-checkType state map. `state` is the BE's
    RegulatoryRiskState. Optional throughout, because polus omits the whole map until
    underwriting-engine is deployed and the cell then falls back to riskProfile.
*/
export interface RegulatoryRiskCell {
    state: string;
    confirmedCount: number;
    probableCount: number;
    // Confirmed-and-open count for THIS row's own entity — lets a Confirmed pill's click
    // handler pick Open vs Disposed without re-deriving the row's group key.
    confirmedOpenCount?: number;
    // Legal-cases-only counts, split by court status. Optional because an old polus (deployed
    // before this cut) sends only the four fields above — see legalCasesRiskState's legacy-shape
    // check, which is keyed on probableOpenCount being absent.
    confirmedDisposedCount?: number;
    probableOpenCount?: number;
    probableDisposedCount?: number;
}

/*
    The ids double as the "state" fed into riskIndicatorUtils' existing DOT_COLOR_BY_STATE map
    (RiskIndicatorCard's dot) — CONFIRMED/PROBABLE/NOT_AVAILABLE line up with the wire enum
    values it already handles, NO_RISK is reused for No Match (the same "nothing found" signal),
    and NO_OPEN is the one genuinely new entry that map needs.
*/
export type LegalCasesStateId = 'CONFIRMED' | 'PROBABLE' | 'NO_OPEN' | 'NO_RISK' | 'NOT_AVAILABLE';

export interface LegalCasesRiskState {
    id: LegalCasesStateId;
    label: string;
    /** Second, smaller line — a disposed-case count. Absent means don't render one at all. */
    subLabel?: string;
    /** Absent on the two non-clickable states (No Data, No Match). */
    tooltip?: string;
    openable: boolean;
    tick: boolean;
    color: string;
    backgroundColor: string;
    /** Table section scrollToGroup should land on when this state is clicked; unset when not openable. */
    group?: GroupId;
}

export interface Row {
    docId?: string;
    name: string;
    type: string;
    riskProfile: RiskProfile;
    riskState?: Record<string, RegulatoryRiskCell>;
    designation?: string;
    identifier?: string;
    shareholdingPercentage?: number | null;
    // polus attaches this per person row (injectAmlPepDesignation, RECUR-27924) — the same
    // profile the hover card and the status pill both read.
    directorProfile?: DirectorHoverProfile;
}

export interface RegulatoryRiskDrawerState {
    row: Row;
    checkType: string;
}

export interface RegulatoryRiskHitsResult {
    matchCount: number;
    matches: Record<string, any>[];
    // RECUR-27829. `matches` stays as the union of both sections so nothing that read it
    // before breaks; the drawer renders from these two.
    confirmedMatches?: Record<string, any>[];
    probableMatches?: Record<string, any>[];
}

export interface ViewerState {
    url: string;
    mimeType: string;
    heading: string;
    docId: string;
}

const RiskIndicators = ({
    isPDFReport = false,
    onOpenLegalCases,
}: {
    isPDFReport?: boolean;
    onOpenLegalCases?: (row: Row, cell: RegulatoryRiskCell, direction: Direction) => void;
}) => {
    const [rows, setRows] = useState<Row[]>([]);

    const {
        isPolling,
        isFetchFailed,
        statusMessage,
        lastUpdatedLabel,
        RiskDotMessage,
        regulatoryRiskDrawerState,
        regulatoryRiskHits,
        regulatoryRiskHitsLoading,
        onOpenRegulatoryRisk,
        closeRegulatoryRiskDrawer,
        regulatoryRiskDrawerConfig,
    } = useRiskIndicatorStates({
        rows,
        setRows,
        autoFetch: true,
    });

    // Routes the pill's open callback by checkType: by design, both Legal Cases columns scroll
    // to the matching table section (in the clicked column's own direction) instead of opening
    // the regulatory-risk drawer.
    const handleOpenRegulatoryRisk = (row: Row, checkType: string) => {
        if (LEGAL_CASES_KEYS.has(checkType)) {
            const cell = row.riskState?.[checkType];
            if (!cell) return;
            onOpenLegalCases?.(row, cell, LEGAL_CASES_DIRECTION_BY_KEY[checkType]);
            return;
        }
        onOpenRegulatoryRisk(row, checkType);
    };

    const showFailureIndicator = !isPDFReport && isFetchFailed;

    const headerRight = (
        <div className={styles.DataMessage}>
            <span className={styles.HeaderIndicator} data-testid={showFailureIndicator ? 'fetch-failure-indicator' : undefined}>
                {isPolling && <LoadingSpinner color="#6021B3" height="1.125rem" />}
                {showFailureIndicator && (
                    <>
                        <FetchFailureIcon className={styles.FailureIcon} />
                        <span className={clsx(styles.Message, styles.Failure)}>{FETCH_FAILURE_TEXT}</span>
                    </>
                )}
                {statusMessage && <span className={styles.Message}>{statusMessage}</span>}
            </span>
            {lastUpdatedLabel && <div className={styles.UpdateDate}>{lastUpdatedLabel}</div>}
        </div>
    );

    const titleWithTooltip = (
        <div className={styles.HeadingMessage}>
            <span>Regulatory Checks</span>
            <span className={styles.InfoIcon}>
                {!isPDFReport && (
                    <Tooltip
                        content={RISK_MESSAGES_MAP[RISK_HEADER_KEYS.RISK_INDICATOR_KEY] || ''}
                        direction="right"
                        style={{ top: '100%' }}
                    >
                        <img src={infoToolTip} height={'14px'} />
                    </Tooltip>
                )}
            </span>
        </div>
    );

    // Failure state (delta 6) renders the card as header-only — table, cards, and footer all
    // collapse to nothing, independent of whatever rows happen to be in state.
    const bodyContent = !isFetchFailed && rows?.length > 0 && (
        <>
            {isPDFReport ? (
                <div className={styles.CardsContainer}>
                    {rows.map((row, index) => (
                        <RiskIndicatorCard key={index} row={row} />
                    ))}
                </div>
            ) : (
                <div className={styles.TableWrapper}>
                    <div className={styles.TableContainer}>
                        <RiskIndicatorTable
                            rows={rows}
                            setRows={setRows}
                            isPDFReport={isPDFReport}
                            onOpenRegulatoryRisk={handleOpenRegulatoryRisk}
                        />
                    </div>
                </div>
            )}
            <div className={styles.FooterContainer}>
                <div className={styles.FooterMessage}>
                    <span className={styles.Note}>Note:</span> Risks are flagged based on the legal name match (please verify).
                </div>
                {/* <div className={styles.RiskDotMessage}>{RiskDotMessage()}</div> */}
            </div>
        </>
    );

    const render = (
        <div className={clsx(styles.ContainerWrapper, { [styles.PDFReport]: isPDFReport })}>
            <HeaderBodyBox
                title={!isPDFReport ? titleWithTooltip : 'Regulatory Checks'}
                headerRight={headerRight}
                className={clsx({ [styles.DataAvailable]: rows?.length > 0 })}
                isPDFReport={isPDFReport}
                hideBody={isFetchFailed}
            >
                {bodyContent}
            </HeaderBodyBox>
        </div>
    );

    if (isPDFReport) return rows?.length ? <A4Page sectionName={REPORT_SECTION_NAMES.REGULATORY_INDICATORS}>{render}</A4Page> : <></>;

    return (
        <>
            {render}
            {!!regulatoryRiskDrawerState && (
                <RegulatoryRiskDrawer
                    checkType={regulatoryRiskDrawerState.checkType}
                    entityName={regulatoryRiskDrawerState.row?.name}
                    matches={regulatoryRiskHits?.matches ?? []}
                    confirmedMatches={regulatoryRiskHits?.confirmedMatches}
                    probableMatches={regulatoryRiskHits?.probableMatches}
                    config={regulatoryRiskDrawerConfig?.[regulatoryRiskDrawerState.checkType]}
                    isLoading={regulatoryRiskHitsLoading}
                    onClose={closeRegulatoryRiskDrawer}
                />
            )}
        </>
    );
};

export default RiskIndicators;
